import { XieziPies } from "./xiezi-pies.js";
import type { XieziSquare } from "./xiezi-square.js";
import type { DrillParts } from "./xiezi-view.js";

/**
 * Finding the pieces of a drill block, by the names the markup gives them,
 * and joining its buttons to whatever drives the drill.
 *
 * `src/cidian-page/cidian-xiezi-markup.ts` marks each with a `data-cb-xiezi-`
 * attribute, and `partials/lianxi/drill.html` marks an Exercise's block the
 * same way, so one reader serves both.
 */

/** What the block's buttons drive: the drill on an entry, or an Exercise. */
export interface DrillControls {
  undo(): void;
  clear(): void;
  check(): Promise<void>;
  again(): void;
}

/**
 * What the block says about itself under a `data-cb-` name, or the empty
 * string where the markup said nothing.
 */
export function said(block: HTMLElement, name: string): string {
  return block.dataset[name] ?? "";
}

/**
 * The recordings an element names under `data-cb-xiezi-say`, one address per
 * voice and separated by spaces, or none where it names none.
 */
export function recordingsOf(element: HTMLElement | undefined): string[] {
  return (element?.dataset["cbXieziSay"] ?? "")
    .split(" ")
    .filter((address) => address !== "");
}

/**
 * The reading and meaning an element gives its character under
 * `data-cb-xiezi-prompt`, which a stage that hides the character says
 * instead.
 */
export function promptOf(element: HTMLElement | undefined): string | undefined {
  const prompt = element?.dataset["cbXieziPrompt"];

  return prompt === "" ? undefined : prompt;
}

/** One control or region inside the block, by name. */
export function part(within: Element, name: string): HTMLElement | undefined {
  const found = within.querySelector(`[data-cb-xiezi-${name}]`);

  return found instanceof HTMLElement ? found : undefined;
}

/** One button inside the block, by name. */
export function button(
  within: Element,
  name: string,
): HTMLButtonElement | undefined {
  const found = part(within, name);

  return found instanceof HTMLButtonElement ? found : undefined;
}

/** The controls the block carries, where the markup drew all of them. */
export function drillParts(
  block: HTMLElement,
  square: XieziSquare,
): DrillParts | undefined {
  const stage = part(block, "stage");
  const note = part(block, "note");
  const check = button(block, "check");
  const again = button(block, "again");
  const pies = part(block, "pies");

  return stage && note && check && again
    ? {
        block,
        square,
        stage,
        note,
        check,
        again,
        undo: button(block, "undo"),
        clear: button(block, "clear"),
        pies: pies === undefined ? undefined : new XieziPies(pies),
      }
    : undefined;
}

/**
 * Join the block's buttons and the window's resize to what drives the drill.
 * @param block - The block.
 * @param controls - What a press does.
 * @param square - The square, which the resize fits to its box again.
 */
export function wireControls(
  block: HTMLElement,
  controls: DrillControls,
  square: XieziSquare,
): void {
  button(block, "undo")?.addEventListener("click", () => {
    controls.undo();
  });
  button(block, "clear")?.addEventListener("click", () => {
    controls.clear();
  });
  button(block, "check")?.addEventListener("click", () => {
    void controls.check();
  });
  button(block, "again")?.addEventListener("click", () => {
    controls.again();
  });
  block.ownerDocument.defaultView?.addEventListener("resize", () => {
    square.resize();
  });
}
